<script setup lang="ts">
import {
  autoPlacement,
  autoUpdate,
  offset,
  useFloating,
} from '@floating-ui/vue';
import {
  tpgIconCheck,
  tpgIconCircle,
  tpgIconClose,
  tpgIconEdit,
  tpgIconRectangle,
} from '@tpg/grammar-web-icons';
import { computed, ref } from 'vue';

import {
  GrammarDivider,
  GrammarOverlayCard,
  GrammarQuaternaryButton,
} from '@nora-monorepo/grammar-ui';
import {
  t,
  useConfirmModalStore,
} from '@nora-monorepo/ui-shared';

import EditingFillTab from '#nora-ui/map/components/drawtools/editing/EditingFillTab.vue';
import EditingInfoTab from '#nora-ui/map/components/drawtools/editing/EditingInfoTab.vue';
import EditingLineTab from '#nora-ui/map/components/drawtools/editing/EditingLineTab.vue';
import type { DrawTool } from '#nora-ui/map/drawtools/types';
import { useMapStore } from '#nora-ui/map/stores/map-store';
import { useGeofenceStore } from '#nora-ui/stores/geofence-store';

export interface EditingToolbarViewProps {
  drawTool: DrawTool;
  mapId: string;
}

const props = defineProps<EditingToolbarViewProps>();

enum ToolbarTab {
  Fill = 'fill',
  Line = 'line',
  Info = 'info',
}

const geofenceStore = useGeofenceStore();
const fillStyleReference = ref();
const lineStyleReference = ref();
const openToolBar = ref<ToolbarTab | null>(
  props.drawTool.drawToolName === 'Symbol' ? ToolbarTab.Info : null,
);
const toolbarReference = ref();
const mapStore = useMapStore();
const map = mapStore.getMapByIdOrThrow(props.mapId);
const drawToolUI = map.drawToolUI;
const confirmModalStore = useConfirmModalStore();

const isSymbolTool = computed(() => props.drawTool.drawToolName === 'Symbol');

const showInfoTab = computed(() => props.drawTool.showInfoTab() && openToolBar.value === ToolbarTab.Info);

useFloating(toolbarReference, fillStyleReference, {
  whileElementsMounted: autoUpdate,
  placement: 'top',
  middleware: [
    offset(5),
    autoPlacement({
      alignment: 'start',
      allowedPlacements: ['top'],
    }),
  ],
  transform: false,
});

useFloating(toolbarReference, lineStyleReference, {
  whileElementsMounted: autoUpdate,
  placement: 'top',
  middleware: [
    offset(5),
    autoPlacement({
      alignment: 'start',
      allowedPlacements: ['top'],
    }),
  ],
  transform: false,
});

function toggleTab(tab: ToolbarTab) {
  openToolBar.value = openToolBar.value === tab ? null : tab;
}

function closeTab() {
  openToolBar.value = null;
}

function closeInfoTab() {
  if (isSymbolTool.value) {
    openToolBar.value = ToolbarTab.Info;
    return;
  }

  closeTab();
}

async function onSave(): Promise<void> {
  await props.drawTool.save();
  props.drawTool.dismiss();
}

function onCancel() {
  if (!drawToolUI.isEditing.value) {
    props.drawTool.dismiss();
    geofenceStore.isGeofenceFeatureSelectionActive = false;
    return;
  }

  if (
    drawToolUI.activeDrawToolType.value !== null
    && drawToolUI.activeDrawTool?.value?.isEdited
  ) {
    confirmModalStore.showConfirmModal({
      headerText: 'Discard changes?',
      bodyText: 'Unsaved edits will be lost.',
      confirmButtonLabel: t('common.discard'),
      onConfirm: () => {
        drawToolUI.revertEdit();
      },
    });
  }
  else {
    drawToolUI.revertEdit();
  }
}
</script>

<template>
  <EditingFillTab
    v-if="openToolBar === ToolbarTab.Fill"
    ref="fillStyleReference"
    :ignored-click-outside-elements="[toolbarReference]"
    :map-id="props.mapId"
    @close="closeTab"
  />
  <EditingLineTab
    v-else-if="openToolBar === ToolbarTab.Line"
    ref="lineStyleReference"
    :ignored-click-outside-elements="[toolbarReference]"
    :map-id="props.mapId"
    @close="closeTab"
  />
  <EditingInfoTab
    v-else-if="showInfoTab"
    :draw-tool="props.drawTool"
    :ignored-click-outside-elements="[toolbarReference]"
    @close="closeInfoTab"
  />
  <div class="pt-2 flex items-center justify-center w-full">
    <GrammarOverlayCard ref="toolbarReference">
      <div class="flex flex-row justify-around items-center min-h-12.5">
        <div
          v-if="props.drawTool.showStyles()"
          class="flex flex-row"
        >
          <template v-if="props.drawTool.showFillColor()">
            <GrammarQuaternaryButton
              :icon-template="tpgIconRectangle"
              :icon-color="drawToolUI.fillColor.value"
              :activated="openToolBar === ToolbarTab.Fill"
              @click="toggleTab(ToolbarTab.Fill)"
            />
            <GrammarDivider orientation="vertical" />
          </template>

          <GrammarQuaternaryButton
            v-if="props.drawTool.showLineColor()"
            :icon-template="tpgIconCircle"
            :icon-color="drawToolUI.lineColor.value"
            :activated="openToolBar === ToolbarTab.Line"
            @click="toggleTab(ToolbarTab.Line)"
          />
          <GrammarDivider orientation="vertical" />
          <GrammarQuaternaryButton
            v-if="props.drawTool.showInfoTab()"
            :icon-template="tpgIconEdit"
            :activated="openToolBar === ToolbarTab.Info"
            @click="toggleTab(ToolbarTab.Info)"
          />
          <GrammarDivider orientation="vertical" />
        </div>
        <div class="flex flex-row gap-2">
          <GrammarQuaternaryButton
            :icon-template="tpgIconCheck"
            @click="onSave"
          />
          <GrammarQuaternaryButton
            :icon-template="tpgIconClose"
            @click="onCancel"
          />
        </div>
      </div>
    </GrammarOverlayCard>
  </div>
</template>
